import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import {
    FidelitySmaRetirementOptions,
    RetirementArrangement,
    RetirementPacket,
    RetirementParticipant,
    RetirementPlanType,
    RetirementStructure,
    RetirementTaxIdType,
} from '@ndvr/customer-api';

import {
    FidelityApplicationSection,
    FidelityCompletionRequirements,
    FidelityDownloadButton,
    FidelityDownloadFeedback,
    FidelityPacketCard,
} from '@/components/fidelity/FidelityApplicationComponents';
import { Button } from '@/components/ui/button';
import { CustomerApi } from 'API';

import { accountTitle } from './account-model';
import { FidelitySmaSectionProps } from './useFidelitySmaApplications';

const controlClass = 'border-border rounded border bg-white p-2 text-sm';
const planTypes: Record<RetirementPlanType, string> = {
    PLAN_401K: '401(k)',
    PROFIT_SHARING: 'Profit sharing',
    MONEY_PURCHASE: 'Money purchase',
    DEFINED_BENEFIT_PENSION: 'Defined benefit / pension',
    CASH_BALANCE: 'Cash balance',
    PLAN_403B: '403(b)',
    GOVERNMENT_403B_457B: 'Government-sponsored 403(b) / 457(b)',
    TAX_EXEMPT_EMPLOYER_457B_457F: 'Tax-exempt employer 457(b) / 457(f)',
    VEBA: 'VEBA',
    ESOP: 'ESOP',
    OWNER_ONLY_401K: 'Owner-only 401(k)',
    OWNER_ONLY_PROFIT_SHARING: 'Owner-only profit sharing',
    OWNER_ONLY_MONEY_PURCHASE: 'Owner-only money purchase',
    OWNER_ONLY_DEFINED_BENEFIT: 'Owner-only defined benefit / pension',
    POOLED_IRA: 'Nonprototype IRA arrangement (not a Premiere Select IRA)',
};
const roles = [
    ['trusteeCustodian', 'Trustee / custodian'],
    ['grantor', 'Living grantor'],
    ['deceasedGrantor', 'Deceased grantor (name only)'],
    ['appointRemove', 'Appoint / remove trustees'],
    ['revokeAmend', 'Revoke / amend authority'],
    ['controlPerson', 'Control person'],
    ['entityOwner', 'Qualifying entity owner'],
] as const;

export const FidelityRetirementApplicationView = ({
    household,
    applications,
    action,
    ssnAccess,
    checkingAccess,
}: FidelitySmaSectionProps<'retirement'>) => {
    const accountGUIDs = household.accounts.map(({ account }) => account.accountGUID);

    const title = (guid: string) => {
        const row = household.accounts.find(({ account }) => account.accountGUID === guid);
        return row ? accountTitle(row.account) : 'Account no longer available';
    };
    return (
        <FidelityApplicationSection
            name="Nonprototype Retirement"
            hasAccounts={accountGUIDs.length > 0}
            groupCount={applications.data?.applications.length}
            checkingAccess={checkingAccess}
            ssnAccess={!!ssnAccess}
            applications={applications}
        >
            <>
                {applications.data?.applications.map((packet) => (
                    <RetirementDraft key={JSON.stringify(packet)} packet={packet} title={title} action={action} />
                ))}
            </>
            {!!ssnAccess && accountGUIDs.length > 0 && (
                <Button variant="outline" onClick={() => void applications.refetch()}>
                    Refresh Retirement applications
                </Button>
            )}
        </FidelityApplicationSection>
    );
};

const RetirementDraft = ({
    packet,
    title,
    action,
}: {
    packet: RetirementPacket;
    title: (guid: string) => string;
    action: FidelitySmaSectionProps<'retirement'>['action'];
}) => {
    const [options, setOptions] = useState<FidelitySmaRetirementOptions>({
        participants: [],
        sharedPlanConfirmed: false,
    });
    const [selectedAccounts, setSelectedAccounts] = useState(packet.accountGUIDs);
    const generate = useMutation({
        mutationFn: () =>
            CustomerApi({ hideError: true }).getSmaRetirementForm(options, { accountGUIDs: selectedAccounts }),
    });
    const update = (patch: Partial<FidelitySmaRetirementOptions>) => {
        generate.reset();
        setOptions((old) => ({ ...old, ...patch }));
    };
    const updateParty = (guid: string, patch: Partial<RetirementParticipant>) => {
        const previous = options.participants.find((p) => p.personOrgGUID === guid) ?? {
            personOrgGUID: guid,
            trusteeCustodian: false,
            grantor: false,
            deceasedGrantor: false,
            appointRemove: false,
            revokeAmend: false,
            controlPerson: false,
            entityOwner: false,
        };
        const changed = { ...previous, ...patch };
        update({
            participants: (options.participants.some((p) => p.personOrgGUID === guid)
                ? options.participants.map((p) => (p.personOrgGUID === guid ? changed : p))
                : [...options.participants, changed]
            ).filter((p) => roles.some(([key]) => p[key])),
        });
    };
    return (
        <FidelityPacketCard name="Nonprototype Retirement" accountCount={selectedAccounts.length}>
            <fieldset disabled={generate.isPending} className="flex flex-col gap-4">
                <legend className="mb-3 font-medium">Choose accounts for this packet</legend>
                {packet.accountGUIDs.map((guid) => (
                    <div key={guid} className="flex flex-wrap items-center justify-between gap-4">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={selectedAccounts.includes(guid)}
                                onChange={(e) => {
                                    generate.reset();
                                    setSelectedAccounts(
                                        packet.accountGUIDs.filter((id) =>
                                            id === guid ? e.target.checked : selectedAccounts.includes(id),
                                        ),
                                    );
                                    update({ sharedPlanConfirmed: false });
                                }}
                            />
                            {title(guid)}
                        </label>
                        {action(guid)}
                    </div>
                ))}
                {selectedAccounts.length > 1 && (
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={options.sharedPlanConfirmed}
                            onChange={(e) => update({ sharedPlanConfirmed: e.target.checked })}
                        />
                        I confirm these accounts share the same plan, pooled/FBO participant, parties and Section 4
                        elections.
                    </label>
                )}
                <p className="text-cocoa-600 text-sm">Download-only choices. Leave unknown details blank.</p>
                <label className="flex flex-col gap-1">
                    Plan type
                    <select
                        className={controlClass}
                        value={options.planType ?? ''}
                        onChange={(e) => update({ planType: (e.target.value as RetirementPlanType) || undefined })}
                    >
                        <option value="">Complete manually</option>
                        {Object.entries(planTypes).map(([value, label]) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1">
                    Subject to ERISA
                    <select
                        className={controlClass}
                        value={options.erisa === undefined ? '' : String(options.erisa)}
                        onChange={(e) =>
                            update({
                                erisa: e.target.value === '' ? undefined : e.target.value === 'true',
                                participants: [],
                            })
                        }
                    >
                        <option value="">Complete manually</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </label>
                <label className="flex flex-col gap-1">
                    Established under
                    <select
                        className={controlClass}
                        value={options.structure ?? ''}
                        onChange={(e) =>
                            update({
                                structure: (e.target.value as RetirementStructure) || undefined,
                                participants: [],
                            })
                        }
                    >
                        <option value="">Complete manually</option>
                        <option value="TRUST">Trust</option>
                        <option value="CUSTODIAN">Custodian</option>
                    </select>
                </label>
                <p className="text-sm">Changing ERISA or structure resets selected roles.</p>
                <label className="flex flex-col gap-1">
                    Account arrangement
                    <select
                        className={controlClass}
                        value={options.arrangement ?? ''}
                        onChange={(e) =>
                            update({
                                arrangement: (e.target.value as RetirementArrangement) || undefined,
                                fboPersonGUID: undefined,
                            })
                        }
                    >
                        <option value="">Complete manually</option>
                        <option value="POOLED">Pooled</option>
                        <option value="FBO">For benefit of one participant (FBO)</option>
                    </select>
                </label>
                {options.arrangement === RetirementArrangement.FBO && (
                    <label className="flex flex-col gap-1">
                        FBO participant
                        <select
                            className={controlClass}
                            value={options.fboPersonGUID ?? ''}
                            onChange={(e) => update({ fboPersonGUID: e.target.value || undefined })}
                        >
                            <option value="">Complete manually</option>
                            {packet.linkedParties
                                .filter((p) => p.selectable && !p.deceased)
                                .map((p) => (
                                    <option key={p.personOrgGUID} value={p.personOrgGUID}>
                                        {p.name}
                                    </option>
                                ))}
                        </select>
                    </label>
                )}
                {(
                    [
                        ['planName', 'Full plan/trust name, including plan type'],
                        ['sponsoringEmployer', 'Sponsoring employer name'],
                        ['planDate', 'Plan/trust date'],
                    ] as const
                ).map(([key, label]) => (
                    <label key={key} className="flex flex-col gap-1">
                        {label}
                        <input
                            className={controlClass}
                            type={key === 'planDate' ? 'date' : 'text'}
                            value={options[key] ?? ''}
                            onChange={(e) => update({ [key]: e.target.value || undefined })}
                        />
                    </label>
                ))}
                <label className="flex flex-col gap-1">
                    Use the saved plan owner’s tax ID as
                    <select
                        className={controlClass}
                        value={options.taxIdType ?? ''}
                        onChange={(e) => update({ taxIdType: (e.target.value as RetirementTaxIdType) || undefined })}
                    >
                        <option value="">Complete plan tax ID manually</option>
                        <option value="ENTITY_TIN">Confirmed plan/trust EIN/TIN</option>
                        <option value="SSN_ITIN">Confirmed sole proprietor SSN/ITIN</option>
                    </select>
                </label>
                <label className="flex flex-col gap-1">
                    Is this a nonqualified plan for a for-profit employer?
                    <select
                        className={controlClass}
                        value={options.nonqualifiedForProfit === undefined ? '' : String(options.nonqualifiedForProfit)}
                        onChange={(e) =>
                            update({
                                nonqualifiedForProfit: e.target.value === '' ? undefined : e.target.value === 'true',
                            })
                        }
                    >
                        <option value="">Confirm manually</option>
                        <option value="false">No</option>
                        <option value="true">Yes — this application cannot be used</option>
                    </select>
                </label>
                <p className="text-cocoa-600 text-sm">
                    Select confirmed roles: two people and one additional non-ERISA custodial owner. Other parties need
                    manual completion.
                </p>
                {packet.linkedParties.map((party) => {
                    const selected = options.participants.find((p) => p.personOrgGUID === party.personOrgGUID);
                    return (
                        <div key={party.personOrgGUID} className="border-border rounded border p-3">
                            <p className="font-medium">{party.name}</p>
                            <p className="text-cocoa-600 text-sm">
                                Saved: {party.savedRoles}
                                {party.deceased ? ' — deceased' : ''}
                            </p>
                            {party.selectable ? (
                                <div className="mt-2 flex flex-wrap gap-3">
                                    {roles
                                        .filter(([key]) =>
                                            key === 'trusteeCustodian'
                                                ? !party.deceased
                                                : options.erisa === false &&
                                                  (options.structure === RetirementStructure.TRUST
                                                      ? !['controlPerson', 'entityOwner'].includes(key) &&
                                                        (!party.deceased || key === 'deceasedGrantor')
                                                      : options.structure === RetirementStructure.CUSTODIAN &&
                                                        ['controlPerson', 'entityOwner'].includes(key) &&
                                                        !party.deceased),
                                        )
                                        .map(([key, label]) => (
                                            <label key={key} className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={selected?.[key] ?? false}
                                                    onChange={(e) =>
                                                        updateParty(party.personOrgGUID, { [key]: e.target.checked })
                                                    }
                                                />
                                                {label}
                                            </label>
                                        ))}
                                    {selected &&
                                        options.structure === RetirementStructure.CUSTODIAN &&
                                        options.erisa === false && (
                                            <label className="flex items-center gap-2">
                                                Title
                                                <input
                                                    className={controlClass}
                                                    value={selected.title ?? ''}
                                                    onChange={(e) =>
                                                        updateParty(party.personOrgGUID, { title: e.target.value })
                                                    }
                                                />
                                            </label>
                                        )}
                                </div>
                            ) : (
                                <p className="text-sm">Manual completion required.</p>
                            )}
                        </div>
                    );
                })}
                {!!options.participants.length && (
                    <ol className="list-inside list-decimal text-sm" aria-label="Selected participant order">
                        {options.participants.map((p) => (
                            <li key={p.personOrgGUID}>
                                {packet.linkedParties.find((linked) => linked.personOrgGUID === p.personOrgGUID)?.name}
                                {p.trusteeCustodian
                                    ? ' — trustee/custodian; signature name included'
                                    : ' — no signature authority'}
                            </li>
                        ))}
                    </ol>
                )}
            </fieldset>
            <FidelityCompletionRequirements requirements={packet.completionRequirements} beforeChoices />
            <FidelityDownloadButton
                download={generate}
                variant="outline"
                disabled={
                    !selectedAccounts.length ||
                    options.nonqualifiedForProfit === true ||
                    (selectedAccounts.length > 1 && !options.sharedPlanConfirmed)
                }
                onClick={() => generate.mutate()}
            >
                Generate SMA Nonprototype Retirement Application
            </FidelityDownloadButton>
            <FidelityDownloadFeedback download={generate} />
        </FidelityPacketCard>
    );
};
